import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Wallet as WalletIcon, Upload, ShieldCheck, AlertTriangle } from "lucide-react";
import api, { getErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { NeonButton, NeonCard, NeonPrice, SectionTitle, Spinner, Pill } from "../components/Neon";
import { gbp, fmtDateTime } from "../lib/format";
import { getLimits, saveLimits, setCooloff, isCoolingOff, getCooloffUntil, COOLOFF_OPTIONS } from "../lib/responsible";

export default function WalletPage() {
  const { refreshUser } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cashoutAmt, setCashoutAmt] = useState("");
  const [busy, setBusy] = useState(false);
  const [docType, setDocType] = useState("passport");
  const [limits, setLimits] = useState(getLimits());
  const fileRef = useRef(null);

  const load = () => api.get("/wallet").then(({ data }) => setWallet(data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File too large (max 5MB)."); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      setBusy(true);
      try {
        await api.post("/kyc/upload", { doc_type: docType, document_base64: reader.result });
        toast.success("Document uploaded — pending review.");
        load();
      } catch (err) { toast.error(getErr(err)); } finally { setBusy(false); }
    };
    reader.readAsDataURL(file);
  };

  const requestCashout = async () => {
    const amt = Number(cashoutAmt);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount."); return; }
    setBusy(true);
    try {
      await api.post("/wallet/cashout", { amount: amt });
      toast.success("Cash-out requested.");
      setCashoutAmt("");
      load(); refreshUser();
    } catch (err) { toast.error(getErr(err)); } finally { setBusy(false); }
  };

  const doSaveLimits = async () => {
    saveLimits(limits); // keep locally for instant client-side spend checks
    try {
      await api.put("/me/responsible-gambling", {
        daily_limit: Number(limits.daily) || null,
        weekly_limit: Number(limits.weekly) || null,
        monthly_limit: Number(limits.monthly) || null,
      });
      toast.success("Spend limits saved.");
    } catch (e) {
      toast.success("Spend limits saved on this device.");
    }
  };
  const doCooloff = async (opt) => {
    if (!window.confirm(`Start a cool-off for ${opt.label}? This can't be undone early.`)) return;
    setCooloff(opt.ms);
    const until = new Date(Date.now() + opt.ms).toISOString();
    try { await api.put("/me/responsible-gambling", { cool_off_until: until }); } catch (e) { /* enforced locally too */ }
    toast.success(`Cool-off active for ${opt.label}.`);
    setLimits({ ...limits });
  };

  if (loading) return <Spinner className="min-h-[60vh]" />;

  const kyc = wallet?.kyc_status || "none";
  const kycColor = kyc === "approved" ? "green" : kyc === "pending" ? "cyan" : kyc === "rejected" ? "pink" : "grey";

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-10">
      <SectionTitle accent="white"><WalletIcon className="inline h-7 w-7 mr-2 text-[var(--neon-green)]" />Wallet</SectionTitle>

      <div className="mt-6 grid lg:grid-cols-3 gap-5">
        <NeonCard variant="flow" innerClassName="p-6">
          <p className="text-xs uppercase tracking-widest text-[var(--text-muted)]">Balance</p>
          <NeonPrice value={gbp(wallet?.balance)} color="green" className="text-5xl block mt-2" testid="wallet-balance" />
          <p className="mt-3 text-xs text-[var(--text-muted)]">Minimum cash-out {gbp(wallet?.min_cashout)}</p>
          <div className="mt-3"><Pill color={kycColor}>KYC: {kyc}</Pill></div>
        </NeonCard>

        <NeonCard variant="soft" innerClassName="p-6">
          <p className="font-display uppercase text-xl text-white">Request Cash-out</p>
          <p className="mt-1 text-xs text-[var(--text-muted)]">KYC approval required.</p>
          <input data-testid="cashout-amount" type="number" value={cashoutAmt} onChange={(e) => setCashoutAmt(e.target.value)} placeholder="Amount" className="mt-3 w-full bg-[var(--surface-2)] border border-white/10 rounded-xl px-3 py-2.5 text-white focus:outline-none focus:border-[var(--neon-green)]" />
          <NeonButton variant="outlineGreen" className="mt-3 w-full" data-testid="wallet-request-cashout" onClick={requestCashout} loading={busy}>Request Cash-out</NeonButton>
        </NeonCard>

        <NeonCard variant="soft" innerClassName="p-6">
          <p className="font-display uppercase text-xl text-white">Verify Identity (KYC)</p>
          <select value={docType} onChange={(e) => setDocType(e.target.value)} className="mt-3 w-full bg-[var(--surface-2)] border border-white/10 rounded-xl px-3 py-2.5 text-white">
            <option value="passport">Passport</option>
            <option value="driving_licence">Driving Licence</option>
            <option value="national_id">National ID</option>
          </select>
          <input ref={fileRef} data-testid="wallet-kyc-upload" type="file" accept="image/*,application/pdf" onChange={onFile} className="hidden" />
          <NeonButton variant="outlineCyan" className="mt-3 w-full" onClick={() => fileRef.current?.click()} loading={busy}><Upload className="h-4 w-4" /> Upload Document</NeonButton>
        </NeonCard>
      </div>

      {/* Transactions */}
      <div className="mt-6 grid lg:grid-cols-2 gap-5">
        <NeonCard variant="soft" innerClassName="p-6">
          <p className="font-display uppercase text-xl text-white">Transactions</p>
          <div className="mt-3 space-y-2" data-testid="wallet-transactions-table">
            {(wallet?.transactions || []).length === 0 ? <p className="text-sm text-[var(--text-muted)]">No transactions yet.</p> :
              wallet.transactions.map((tx, i) => (
                <div key={i} className="flex items-center justify-between border-b border-white/5 py-2 text-sm">
                  <span className="text-[var(--text-muted)]">{tx.type || tx.description || "Transaction"} · {fmtDateTime(tx.created_at)}</span>
                  <span className={Number(tx.amount) < 0 ? "glow-pink" : "glow-green"}>{gbp(tx.amount)}</span>
                </div>
              ))}
          </div>
        </NeonCard>
        <NeonCard variant="soft" innerClassName="p-6">
          <p className="font-display uppercase text-xl text-white">Cash-outs</p>
          <div className="mt-3 space-y-2">
            {(wallet?.cashouts || []).length === 0 ? <p className="text-sm text-[var(--text-muted)]">No cash-outs yet.</p> :
              wallet.cashouts.map((c, i) => (
                <div key={i} className="flex items-center justify-between border-b border-white/5 py-2 text-sm">
                  <span className="text-[var(--text-muted)]">{fmtDateTime(c.created_at)}</span>
                  <span className="text-white">{gbp(c.amount)}</span>
                  <Pill color={c.status === "approved" ? "green" : c.status === "rejected" ? "pink" : "cyan"}>{c.status}</Pill>
                </div>
              ))}
          </div>
        </NeonCard>
      </div>

      {/* Responsible gambling */}
      <div className="mt-6">
        <NeonCard variant="cyan" innerClassName="p-6">
          <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-[var(--neon-cyan)]" /><p className="font-display uppercase text-xl text-white">Responsible Gambling</p></div>

          {isCoolingOff() && (
            <div className="mt-3 flex items-center gap-2 rounded-lg bg-[rgba(255,45,149,.1)] border border-[rgba(255,45,149,.4)] px-3 py-2 text-sm text-[var(--neon-pink)]">
              <AlertTriangle className="h-4 w-4" /> Cool-off active until {new Date(getCooloffUntil()).toLocaleString("en-GB")}
            </div>
          )}

          <p className="mt-4 text-xs uppercase tracking-widest text-[var(--text-muted)]">Take a break (cool-off)</p>
          <p className="text-xs text-[var(--text-muted)]">Stop yourself entering competitions for a set period. Can't be undone early.</p>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="responsible-gambling-cooloff">
            {COOLOFF_OPTIONS.map((opt) => (
              <button key={opt.label} onClick={() => doCooloff(opt)} className="rounded-full border border-white/15 px-4 py-2 text-sm text-[var(--text-muted)] hover:border-[var(--neon-cyan)] hover:text-white">{opt.label}</button>
            ))}
          </div>

          <p className="mt-6 text-xs uppercase tracking-widest text-[var(--text-muted)]">Spend limits (£)</p>
          <p className="text-xs text-[var(--text-muted)]">Purchases above your limit are blocked. Set 0 to remove a limit.</p>
          <div className="mt-3 grid grid-cols-3 gap-3" data-testid="responsible-gambling-spend-limits">
            {["daily", "weekly", "monthly"].map((k) => (
              <div key={k}>
                <label className="text-xs capitalize text-[var(--text-muted)]">{k}</label>
                <input type="number" value={limits[k]} onChange={(e) => setLimits((s) => ({ ...s, [k]: Number(e.target.value) }))} className="mt-1 w-full bg-[var(--surface-2)] border border-white/10 rounded-xl px-3 py-2.5 text-white focus:outline-none focus:border-[var(--neon-cyan)]" />
              </div>
            ))}
          </div>
          <NeonButton variant="outlinePink" className="mt-4" data-testid="responsible-gambling-save-limits-button" onClick={doSaveLimits}>Save Limits</NeonButton>
          <p className="mt-4 text-xs text-[var(--text-muted)]">Need support? Visit <a href="https://www.begambleaware.org" target="_blank" rel="noreferrer" className="text-[var(--neon-cyan)] underline">BeGambleAware.org</a> or call 0808 8020 133. 18+.</p>
        </NeonCard>
      </div>
    </div>
  );
}
